import React, { useRef, useState, useEffect, DragEvent, ChangeEvent } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { CloudArrowUp, FileEarmark, FileEarmarkCheck } from 'react-bootstrap-icons';

/**
 * FileUpload - универсальный компонент для загрузки файлов с поддержкой drag-and-drop
 *
 * @example
 * // Excel файлы (по умолчанию)
 * <FileUpload
 *   onUpload={async (file) => await uploadExcelOrder(file, customer)}
 *   onUploadSuccess={(data) => console.log('Loaded', data)}
 *   onUploadError={(error) => console.error(error)}
 * />
 *
 * @example
 * // PDF файлы с кастомным размером
 * <FileUpload
 *   onUpload={async (file) => await uploadPdfDocument(file)}
 *   maxFileSize={5 * 1024 * 1024} // 5MB
 *   acceptedFileTypes={['.pdf']}
 *   acceptedMimeTypes=".pdf"
 *   uploadButtonText="Загрузить документ"
 * />
 *
 * @example
 * // Изображения
 * <FileUpload
 *   onUpload={async (file) => await uploadImage(file)}
 *   maxFileSize={2 * 1024 * 1024} // 2MB
 *   acceptedFileTypes={['.jpg', '.jpeg', '.png', '.gif']}
 *   acceptedMimeTypes=".jpg,.jpeg,.png,.gif"
 *   uploadButtonText="Загрузить изображение"
 * />
 */
interface FileUploadProps {
    onUpload: (file: File) => Promise<any>; // Custom upload handler
    onUploadSuccess?: (data: any) => void;
    onUploadError?: (error: string) => void;
    maxFileSize?: number; // in bytes, default 10MB
    acceptedFileTypes?: string[]; // e.g., ['.xlsx', '.xls']
    acceptedMimeTypes?: string; // e.g., '.xlsx,.xls' for input accept attribute
    uploadButtonText?: string; // Custom upload button text
    cancelButtonText?: string; // Custom cancel button text
    autoUpload?: boolean; // Auto-upload file immediately after selection, default true
}

export default function FileUpload({
    onUpload,
    onUploadSuccess,
    onUploadError,
    maxFileSize = 10 * 1024 * 1024, // 10MB default
    acceptedFileTypes = ['.xlsx', '.xls'], // Excel files default
    acceptedMimeTypes = '.xlsx,.xls',
    uploadButtonText = 'Загрузить файл',
    cancelButtonText = 'Отмена',
    autoUpload = true // Auto-upload enabled by default
}: FileUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} Б`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} КБ`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} МБ`;
    };

    const getFileTypeLabel = (): string => {
        if (acceptedFileTypes.includes('.xlsx') || acceptedFileTypes.includes('.xls')) {
            return 'Excel';
        }
        if (acceptedFileTypes.includes('.pdf')) {
            return 'PDF';
        }
        if (acceptedFileTypes.includes('.doc') || acceptedFileTypes.includes('.docx')) {
            return 'Word';
        }
        return acceptedFileTypes.join(', ').toUpperCase();
    };

    const validateFile = (file: File): boolean => {
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!acceptedFileTypes.includes(fileExtension)) {
            const typesLabel = acceptedFileTypes.join(', ');
            setUploadError(`Пожалуйста, выберите файл с расширением: ${typesLabel}`);
            return false;
        }

        if (file.size > maxFileSize) {
            setUploadError(`Файл слишком большой. Максимальный размер ${formatFileSize(maxFileSize)}`);
            return false;
        }

        setUploadError(null);
        return true;
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (validateFile(file)) {
                setSelectedFile(file);
            }
        }
    };

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (validateFile(file)) {
                setSelectedFile(file);
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError('Пожалуйста, выберите файл');
            return;
        }

        setIsUploading(true);
        setUploadError(null);

        try {
            const data = await onUpload(selectedFile);

            if (onUploadSuccess) {
                onUploadSuccess(data);
            }

            // Reset state
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Произошла ошибка при загрузке файла';
            setUploadError(errorMessage);
            if (onUploadError) {
                onUploadError(errorMessage);
            }
        } finally {
            setIsUploading(false);
        }
    };

    // Auto-upload effect: triggers upload when file is selected and autoUpload is enabled
    useEffect(() => {
        if (autoUpload && selectedFile && !isUploading) {
            void handleUpload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFile, autoUpload]);

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="file-upload">
            {uploadError && (
                <Alert variant="danger" dismissible onClose={() => setUploadError(null)}>
                    {uploadError}
                </Alert>
            )}

            <div
                className={`upload-zone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedMimeTypes}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />

                {!selectedFile ? (
                    <div className="upload-prompt">
                        <CloudArrowUp size={48} className="upload-icon" />
                        <p className="upload-text">
                            Перетащите файл {getFileTypeLabel()} сюда<br />
                            или
                        </p>
                        <Button
                            variant="primary"
                            onClick={handleBrowseClick}
                            disabled={isUploading}
                        >
                            <FileEarmark className="me-2" />
                            Выбрать файл
                        </Button>
                        <p className="upload-hint">
                            Максимальный размер: {formatFileSize(maxFileSize)}
                        </p>
                    </div>
                ) : (
                    <div className="file-selected">
                        <div className="file-info">
                            <FileEarmarkCheck size={32} className="file-icon" />
                            <div className="file-details">
                                <p className="file-name">{selectedFile.name}</p>
                                <p className="file-size">{formatFileSize(selectedFile.size)}</p>
                            </div>
                        </div>

                        {isUploading ? (
                            <div className="upload-progress">
                                <Spinner animation="border" size="sm" className="me-2" />
                                <span>Загрузка...</span>
                            </div>
                        ) : (
                            <div className="file-actions">
                                {!autoUpload && (
                                    <Button
                                        variant="success"
                                        onClick={handleUpload}
                                        disabled={isUploading}
                                    >
                                        {uploadButtonText}
                                    </Button>
                                )}
                                <Button
                                    variant="danger"
                                    onClick={handleRemoveFile}
                                    disabled={isUploading}
                                >
                                    {cancelButtonText}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
